"use client";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type Toast = {
  id: number;
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "success" | "info" | "warning";
  duration?: number; // ms
  onClick?: () => void;
};

type ToastCtx = {
  toast: (t: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
};

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((t: Omit<Toast, "id">) => {
    const id = idRef.current++;
    const duration = t.duration ?? 4000;
    setToasts((prev) => [...prev, { ...t, id }].slice(-5));
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <Toasts toasts={toasts} onClose={dismiss} />
    </Ctx.Provider>
  );
}

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useToast must be used within ToastProvider");
  return v;
}

function classForVariant(v?: Toast["variant"]) {
  switch (v) {
    case "destructive":
      return "bg-red-600 text-white";
    case "success":
      return "bg-green-600 text-white";
    case "info":
      return "bg-blue-600 text-white";
    case "warning":
      // Already passed by the video-create held-state toasts; without this
      // case (and the union member) they silently fell through to default.
      return "bg-amber-600 text-white";
    default:
      return "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900";
  }
}

function Toasts({ toasts, onClose }: { toasts: Toast[]; onClose: (id: number) => void }) {
  return (
    <div className="fixed inset-x-0 bottom-3 z-[1000] pointer-events-none flex justify-center px-3">
      <div className="flex flex-col gap-2 w-full max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-md shadow-lg px-3 py-2 ${classForVariant(t.variant)}`}
            role={t.onClick ? 'button' : 'status'}
            aria-live="polite"
            tabIndex={t.onClick ? 0 : -1}
            onClick={t.onClick}
            onKeyDown={(e) => { if (t.onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); t.onClick(); } }}
          >
            {t.title && <div className="font-medium text-sm">{t.title}</div>}
            {t.description && <div className="text-xs opacity-90">{t.description}</div>}
            <button className="absolute right-2 top-1 text-xs opacity-80" onClick={() => onClose(t.id)} aria-label="Close">×</button>
          </div>
        ))}
      </div>
    </div>
  );
}
